import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import {
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
  Warn,
  PL,
} from "../components/contentTemplate";

const SECTIONS = [
  { id: "nfz",           title: "Le NFZ, l'assurance maladie publique" },
  { id: "affiliation",   title: "S'affilier à l'assurance maladie selon votre statut" },
  { id: "soins",         title: "Consulter un médecin et accéder aux soins" },
  { id: "prive",         title: "L'assurance santé privée : LuxMed, Medicover" },
  { id: "cas-pratiques", title: "Dentaire, maternité et médecins francophones" },
  { id: "budget",        title: "Combien coûte la santé en Pologne" },
];

const INTERNAL = "text-[#0056D2] hover:underline";

const SOURCES = [
  {
    url: "https://www.cleiss.fr/docs/regimes/regime_pologne_salaries.html",
    label: "Cleiss, régime de sécurité sociale polonais (salariés)",
    description:
      "Organisation du NFZ et de ses caisses régionales, cotisation santé, libre choix du médecin de famille, remboursement des médicaments.",
  },
  {
    url: "https://www.cleiss.fr/particuliers/partir/vacances/pologne.html",
    label: "Cleiss, se soigner lors d'un séjour en Pologne",
    description:
      "Tiers payant chez les prestataires conventionnés, prise en charge des seuls soins dentaires de base et reste à charge, rôle de la carte européenne d'assurance maladie.",
  },
  {
    url: "https://pacjent.gov.pl/",
    label: "pacjent.gov.pl (portail officiel du patient)",
    description:
      "Liste à jour des spécialistes accessibles sans lettre de référence et dispositif de médicaments gratuits pour les 65 ans et plus et les moins de 18 ans.",
  },
  {
    url: "https://www.nfz.gov.pl/dla-pacjenta/zalatw-sprawe-krok-po-kroku/jak-ubezpieczyc-sie-dobrowolnie/",
    label: "NFZ, assurance volontaire",
    description:
      "Conditions de l'assurance volontaire, cotisation de 9 % d'une base minimale et montant mensuel réévalué chaque trimestre.",
  },
  {
    url: "https://notesfrompoland.com/2025/04/04/polands-sejm-approves-bill-to-cut-health-contributions-for-business-owners/",
    label: "Notes From Poland",
    description:
      "Recours combiné au public et au privé devenu majoritaire, tension sur les délais du système public.",
  },
  {
    url: "https://www.luxmed.pl/en/for-patient/subscriptions",
    label: "Grupa LUX MED, abonnements pour particuliers",
    description:
      "Nature et étendue des abonnements privés (accès aux médecins de premier recours et aux spécialistes, examens).",
  },
  {
    url: "https://www.move-to-poland.com/fr/post/assurance-sante-en-pologne-un-guide-pour-les-expats",
    label: "Move to Poland, guide assurance santé",
    description:
      "Ordres de grandeur des abonnements privés individuels et familiaux sur le marché.",
  },
];

export default function Sante() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="sante-page"
    >
      <header className="mb-10" data-testid="page-hero">
        <span className="hej-chip" data-testid="page-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour en juillet 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Se soigner en Pologne
        </h1>
      </header>

      <section className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12" data-testid="page-lede">
        <p>
          Le système de santé polonais a deux visages. D'un côté, un système public universel, le NFZ, qui couvre tous les
          assurés et donne accès à des soins gratuits. De l'autre, un secteur privé très développé, que plus de la moitié des
          résidents utilisent en complément pour éviter les délais du public. Comprendre comment ces deux mondes s'articulent
          est la clé pour se soigner sereinement.
        </p>
        <p>
          Ce guide s'adresse au francophone qui s'installe pour travailler ou vivre en Pologne, qu'il soit salarié, en famille
          ou tout juste arrivé. Deux cas particuliers sont traités ailleurs sur le site, parce qu'ils obéissent à des règles
          propres : les retraités pensionnés, dont la couverture passe par le document S1, relèvent du guide sur la retraite,
          et les indépendants, dont la cotisation santé dépend du régime fiscal, relèvent de la rubrique Entrepreneuriat.
        </p>
      </section>

      <KeyPointsBlock testid="page-key-points">
        <ul className="list-disc pl-5 space-y-2">
          <li>Le système public NFZ couvre tous les assurés. Il est financé par une cotisation santé de 9 %, prélevée pour les actifs et, chez les salariés, gérée automatiquement par l'employeur.</li>
          <li>Le médecin de famille est votre porte d'entrée. La plupart des spécialistes exigent une lettre de référence, sauf quelques-uns accessibles directement comme le gynécologue, le psychiatre ou le dentiste.</li>
          <li>Les délais dans le public atteignent souvent plusieurs mois pour un spécialiste, ce qui explique le recours massif au privé.</li>
          <li>Un abonnement privé (LuxMed, Medicover) donne un accès rapide et des praticiens souvent anglophones, pour un ordre de grandeur de 150 à 500 zł par mois.</li>
          <li>Le dentaire n'est couvert par le NFZ que pour les soins de base. Les prothèses, les implants et l'esthétique restent à votre charge.</li>
        </ul>
      </KeyPointsBlock>

      <AutoTOC sections={SECTIONS} testid="page-toc" />

      <ContentSection id="nfz" n={1} title="Le NFZ, l'assurance maladie publique">
        <p>
          Le NFZ (<PL>Narodowy Fundusz Zdrowia</PL>), le fonds national de santé, est la colonne vertébrale du système. Il
          finance les soins à travers un office central et seize caisses régionales, une par voïvodie. Tout assuré accède
          gratuitement aux soins chez les prestataires sous contrat avec le NFZ, qu'ils soient publics ou privés, du médecin
          de ville à l'hôpital.
        </p>
        <p>
          Le financement repose sur la cotisation santé (<PL>składka zdrowotna</PL>), fixée à 9 %. Pour un salarié, elle est
          prélevée sur la rémunération et reversée via le ZUS, l'organisme de sécurité sociale. Elle est à la charge du
          salarié, l'employeur n'y participe pas, mais il s'occupe de toute la mécanique de déclaration.
        </p>
        <p>
          Le principe est universel, la réalité est plus nuancée. Le système public est sous tension, avec des files d'attente
          parmi les plus longues de ces dernières années pour certaines consultations. C'est précisément ce qui pousse une
          grande partie de la population à combiner couverture publique et recours au privé, un réflexe désormais majoritaire
          chez les résidents.
        </p>
      </ContentSection>

      <ContentSection id="affiliation" n={2} title="S'affilier à l'assurance maladie selon votre statut">
        <p>Votre porte d'entrée dans le système dépend de votre situation.</p>
        <p>
          Si vous êtes salarié, tout est simple. Votre employeur vous déclare au ZUS dès l'embauche, votre affiliation au NFZ
          en découle, et votre couverture démarre. Vos ayants droit sont couverts avec vous.
        </p>
        <p>
          Si vous rejoignez un proche déjà assuré, vous pouvez être rattaché gratuitement comme membre de sa famille. Le
          rattachement couvre le conjoint, les enfants jusqu'à 18 ans (ou 26 ans s'ils poursuivent des études) et, dans
          certains cas, les parents à charge vivant sous le même toit. La déclaration se fait auprès de l'employeur ou du ZUS.
        </p>
        <p>
          Si vous arrivez sans emploi, votre statut de ressortissant de l'Union ou de Suisse vous permet d'utiliser la carte
          européenne d'assurance maladie pour dépanner pendant une phase courte de transition, car elle couvre les soins
          médicalement nécessaires lors d'un séjour temporaire. Elle ne doit toutefois pas être vue comme une solution durable
          pour vivre en Pologne. Pour une installation dans la durée sans activité, vous pouvez souscrire l'assurance
          volontaire du NFZ (<PL>dobrowolne ubezpieczenie zdrowotne</PL>), un contrat conclu avec le fonds via le ZUS. La
          cotisation représente 9 % d'une base minimale réévaluée chaque trimestre, soit environ 835 zł par mois au deuxième
          trimestre 2026.
        </p>
        <p>
          Deux profils relèvent d'une page dédiée. Si vous venez passer votre retraite, votre couverture passe par le document
          S1 et notre guide pour{" "}
          <Link to={lp("/retraite-pologne")} className={INTERNAL}>prendre sa retraite en Pologne</Link>{" "}
          en détaille le mécanisme. Si vous exercez en indépendant, votre cotisation santé suit votre régime fiscal, un point
          traité dans la rubrique{" "}
          <Link to={lp("/entrepreneuriat")} className={INTERNAL}>Entrepreneuriat</Link>.
        </p>
        <p>
          Si votre couverture NFZ vient d'un emploi salarié, notre page{" "}
          <Link to={lp("/travailler-pologne")} className={INTERNAL}>Travailler en Pologne</Link>{" "}
          explique les contrats qui déclenchent l'affiliation et ce qu'ils changent pour vos droits.
        </p>
      </ContentSection>

      <ContentSection id="soins" n={3} title="Consulter un médecin et accéder aux soins">
        <p>
          Le médecin de famille (<PL>lekarz POZ</PL>) est le pivot du parcours. Vous en choisissez un librement parmi ceux
          sous contrat avec le NFZ, et c'est lui qui vous suit et vous oriente.
        </p>
        <p>
          Pour consulter un spécialiste, la règle générale est la lettre de référence (<PL>skierowanie</PL>) délivrée par le
          médecin de famille. Plusieurs spécialités y échappent et sont accessibles directement, notamment le
          gynécologue-obstétricien, le psychiatre, l'oncologue, le vénérologue et le dentiste, auxquels se sont ajoutés depuis
          2025 le psychologue, l'optométriste et le médecin du sport. Pour la plupart des autres spécialistes, dont le
          dermatologue et l'ophtalmologue, une orientation reste nécessaire, et c'est là que les délais se font sentir,
          parfois plusieurs mois.
        </p>
        <p>
          Entre le médecin de famille et les urgences existe un niveau intermédiaire très utile, la{" "}
          <PL>nocna i świąteczna opieka zdrowotna</PL>, la permanence médicale de nuit, de week-end et de jours fériés. Elle
          prend en charge les problèmes soudains qui ne relèvent pas d'une urgence vitale mais qui ne peuvent pas attendre
          l'ouverture du cabinet.
        </p>
        <p>
          En cas d'urgence, composez le 112, le numéro européen, ou le 999 pour une ambulance. Les urgences hospitalières se
          repèrent sous le sigle SOR. Dans une situation urgente, l'accès au spécialiste ou à l'hôpital est direct, sans
          passer par le médecin de famille.
        </p>
        <p>
          Les pharmacies (<PL>apteka</PL>) sont nombreuses et bien approvisionnées. Sur les médicaments remboursables, une
          participation reste en général à la charge du patient. Un dispositif distinct rend certains médicaments gratuits
          pour les personnes de 65 ans et plus ainsi que pour les moins de 18 ans, à condition que le médicament figure sur
          la liste officielle et corresponde à l'indication médicale prévue.
        </p>
        <p>
          En pratique, le numéro PESEL facilite beaucoup les démarches de santé. Il donne accès à l'
          <PL>Internetowe Konto Pacjenta</PL>, le compte patient en ligne, et à son application <PL>mojeIKP</PL>, où vous
          retrouvez vos ordonnances et vos orientations électroniques ainsi qu'une partie de votre historique médical. À la
          pharmacie, une ordonnance électronique s'utilise le plus souvent avec un code à quatre chiffres et votre numéro
          PESEL, ou via un code QR dans l'application.
        </p>
      </ContentSection>

      <ContentSection id="prive" n={4} title="L'assurance santé privée : LuxMed, Medicover">
        <p>
          Le privé ne remplace pas le NFZ, il le complète, et son usage est devenu la norme plutôt que l'exception. On y va
          pour trois raisons : contourner les délais, obtenir un rendez-vous rapide et trouver facilement des praticiens
          anglophones. Les grands réseaux, LuxMed avec plus de quatre cents centres, Medicover et Enel-Med, couvrent tout le
          pays et concentrent l'essentiel de l'offre.
        </p>
        <p>
          Deux façons d'y recourir. Payer à l'acte, une consultation privée se situant autour de 150 à 350 zł selon la
          spécialité et la ville. Ou souscrire un abonnement, formule la plus répandue, dont l'ordre de grandeur va d'environ
          150 zł par mois pour une couverture individuelle de base à 300 ou 500 zł pour une formule complète incluant
          spécialistes et examens, davantage pour une famille. Beaucoup d'employeurs offrent d'ailleurs un abonnement LuxMed
          ou Medicover comme avantage salarial, pensez à vérifier ce que couvre le vôtre.
        </p>
        <p>
          Attention toutefois à ne pas confondre un abonnement et une assurance santé complète. Selon la formule, l'abonnement
          couvre surtout les consultations, les spécialistes et les examens, mais pas nécessairement une hospitalisation
          lourde, une urgence complexe ou certains traitements coûteux. Lisez précisément le périmètre du contrat avant de
          vous engager.
        </p>
      </ContentSection>

      <ContentSection id="cas-pratiques" n={5} title="Dentaire, maternité et médecins francophones">
        <p>
          Quelques situations réservent des surprises à qui arrive avec les réflexes de son pays d'origine.
        </p>
        <p>
          Le dentaire est le premier. Le NFZ ne prend en charge que les soins de base, comme les contrôles, le traitement des
          caries et les extractions simples. Les prothèses, les implants et tout ce qui touche à l'esthétique sont à votre
          charge, dans le privé. La bonne nouvelle est que les tarifs polonais restent nettement inférieurs à ceux de l'Europe
          de l'Ouest, au point que le pays est une destination de tourisme dentaire.
        </p>
        <p>
          La maternité ensuite. Le suivi de grossesse, l'accouchement et les soins qui suivent sont couverts par le NFZ, et le
          gynécologue s'atteint sans lettre de référence. Beaucoup de familles choisissent malgré tout un suivi privé, pour la
          continuité et le confort.
        </p>
        <p>
          La langue enfin. Dans le privé des grandes villes, le personnel est souvent anglophone. Les praticiens francophones
          existent mais restent rares. L'annuaire en ligne <PL>ZnanyLekarz</PL> permet de filtrer les médecins par langue
          parlée, et votre consulat peut parfois vous orienter vers des praticiens francophones.
        </p>
      </ContentSection>

      <ContentSection id="budget" n={6} title="Combien coûte la santé en Pologne">
        <p>
          Pour un actif, la cotisation santé de 9 % est prélevée à la source et ne demande aucune démarche. Le vrai poste de
          dépense à anticiper est l'abonnement privé, si vous choisissez d'en prendre un, dans les fourchettes évoquées plus
          haut. Pour situer ces montants dans un budget mensuel complet, ville par ville, notre rubrique{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL}>Coût de la vie</Link>{" "}
          vous permet de simuler l'ensemble de vos dépenses.
        </p>
      </ContentSection>

      <Sources sources={SOURCES} testid="page-sources" />

      <Warn testid="page-disclaimer">
        Les informations de ce guide sont fournies à titre indicatif et ne constituent pas un conseil médical. Les règles, les
        montants et les délais évoluent. Pour votre situation, adressez-vous au NFZ, à votre caisse d'assurance ou à un
        professionnel de santé.
      </Warn>
    </article>
  );
}
